import { Brand, NavLinks, UserMenu } from "./NavBar";
import EnvironmentChip from "@/components/common/EnvironmentChip";
import EmergencyStopControl from "@/components/dashboard/EmergencyStopControl";
import PriceTile from "./PriceTile";
import { SystemStateBadge, ToneBadge } from "@/components/common/SemanticBadge";
import type { SystemStateResult } from "@/hooks/useSystemState";
import { useTradingData } from "@/context/TradingDataContext";

/**
 * Badge d'état système du header — la SEULE déclaration de cet état dans l'UI (l'ex-
 * StatusRow du dashboard est mort avec la règle « un fait = une maison »). B7.0 : un
 * transitoire de boot/rollout est CONNECTING (info), jamais ERROR ; le rouge est
 * réservé aux vrais échecs de /api/system/state.
 */
function HeaderSystemBadge({ systemState }: { systemState: SystemStateResult }) {
  if (systemState.connecting && !systemState.error) {
    return (
      <ToneBadge tone="info" dot testId="header-system-state">
        CONNECTING
      </ToneBadge>
    );
  }
  if (systemState.error) {
    return (
      <ToneBadge tone="sell" dot testId="header-system-state">
        ERROR
      </ToneBadge>
    );
  }
  if (systemState.state == null) {
    return <ToneBadge tone="idle" testId="header-system-state">…</ToneBadge>;
  }
  return <SystemStateBadge state={systemState.state} className="header-system-badge" />;
}

/**
 * The single application header — identical on every page. On desktop it is a
 * three-zone bar: Left — brand identity + the environment chip (DEMO/LIVE, between
 * the wordmark and the price) + the live price tile (the bot's pulse, present
 * everywhere). Centre — the emergency-stop kill-switch, deliberately centre-stage
 * so it is always the fastest control to find and hit. Right — the primary
 * navigation (segmented control) + the account menu.
 *
 * <p>On mobile the same elements re-flow into two clean rows (none are ever hidden —
 * nav must survive every breakpoint, C2): row 1 = brand + price + the emergency-stop
 * icon (a compact octagon-X right after the price, growing to fill the row); row 2 =
 * the nav pill on the left and the account avatar on the right. The trick: DOM order
 * == the desktop L→R order (so desktop needs no {@code order} overrides), the two
 * {@code flex-1} centring spacers exist only at {@code sm+} (on a wrapping flex row
 * they don't collapse and would wreck the stack), and mobile just re-orders the
 * children into the two rows. The emergency control itself shrinks to icon-only below
 * {@code sm}.
 *
 * Lives inside {@link AppShell}, which owns the one shared {@code /ws/trading}
 * subscription + system state and feeds them here via {@link useTradingData} — so
 * the header looks and behaves the same whether you're on the Dashboard, History,
 * Analytics, Settings or Logs. (The former dashboard-only TopBar + the ⌘K command
 * palette were removed.)
 */
export default function AppHeader() {
  const { latestPrice, wsStatus, marketStatus, streamHealth, systemState } =
    useTradingData();

  return (
    <header
      className="glass border-border/60 sticky top-0 z-20 flex flex-wrap items-center gap-x-4 gap-y-2.5 border-b px-4 py-2.5 shadow-[0_8px_24px_-16px_rgba(0,0,0,0.9)]"
      aria-label="Application header"
    >
      {/* DOM order == the desktop L→R order (identity · spacer · emergency · spacer ·
          nav · avatar) so desktop needs no `order` overrides; mobile re-orders via
          `order-*` into two rows and hides the flex-1 spacers (which would not
          collapse on a wrapping row). */}

      {/* Identity + environment + live pulse — row 1 left */}
      <div className="order-1 flex min-w-0 items-center gap-3 sm:order-none">
        <Brand />
        <span className="hidden items-center gap-1.5 sm:inline-flex">
          <EnvironmentChip compact />
          <HeaderSystemBadge systemState={systemState} />
        </span>
        <span className="bg-border/70 hidden h-6 w-px sm:block" aria-hidden />
        <PriceTile
          price={latestPrice}
          wsStatus={wsStatus}
          marketStatus={marketStatus}
          streamHealth={streamHealth}
        />
      </div>

      {/* Centring spacer — sm+ only */}
      <div className="hidden flex-1 sm:block" aria-hidden />

      {/* Emergency stop — the one control you must find and hit instantly. On mobile
          it's a compact octagon-X icon sitting inline right after the price (row 1);
          it grows to fill the rest of that row so the nav wraps below it. At sm+ it
          expands to the labelled centre-stage button, sized to content between the
          spacers. */}
      <div className="order-2 flex grow items-center sm:order-none sm:grow-0">
        <EmergencyStopControl
          prominent
          active={systemState.emergencyStop}
          onChanged={systemState.refresh}
        />
      </div>

      {/* Centring spacer — sm+ only */}
      <div className="hidden flex-1 sm:block" aria-hidden />

      {/* Primary navigation — its own row (row 2) on mobile, sharing that row with the
          avatar; right zone on desktop. */}
      <div className="order-3 flex items-center sm:order-none">
        <div className="border-border/50 bg-surface-2/50 flex items-center rounded-xl border p-1 backdrop-blur-sm">
          <NavLinks compact />
        </div>
      </div>

      {/* Account avatar — bottom-right on mobile (row 2, beside the nav), far-right
          after the nav on desktop. Single instance, re-ordered; the separator only
          shows at md+. */}
      <div className="order-4 ml-auto flex items-center gap-2.5 sm:order-none sm:ml-0">
        <span className="bg-border/70 hidden h-6 w-px md:block" aria-hidden />
        <UserMenu />
      </div>
    </header>
  );
}
